import { format } from 'date-fns/format';
import { formatDistanceStrict as formatDistanceToNow } from 'date-fns/formatDistanceStrict';
import komapping from 'knockout.mapping';
import ko from 'knockout';
import MicroModal from 'micromodal';
import { MeetingStatus } from './enums';
import { MeetingDto } from './interfaces';
import { Log } from './log';
import { LocaleUtils } from './locale-utils';
import { R } from './Resources.resx';
import { SetMeetingRoomMessageModalViewModel } from './set-message/set-message';
import { StringUtils } from './string-utils';

type Participant = {
	readonly displayName: string;
	readonly isCaregiver: boolean;
};
class MeetingViewModel {
	public videoRoomKey = ko.observable<string>();
	public cprFormatted = ko.observable<string>();
	public lastActivity = ko.observable<Date>();
	public firstCurrentUserJoinedOn = ko.observable<Date>();
	public createdOn = ko.observable<Date>();
	public url = ko.observable<string>();
	public caregiverHasJoined = ko.observable<boolean>();
	public confirmJoinInput = ko.observable<string>();
	public showConfirmJoinError = ko.observable<boolean>();

	private _participants = ko.observableArray<Participant>();
	private _startTime = ko.observable<Date>();
	private _endTime = ko.observable<Date>();
	private _status = ko.observable<MeetingStatus>();

	public participants = ko.pureComputed(() =>
		this._participants()
			.map((p) => {
				if (p.isCaregiver) {
					return p.displayName + ' ' + R.IsEmployee;
				}
				return p.displayName;
			})
			.join(', '),
	);

	public startTime = ko.pureComputed(() => format(this._startTime(), 'HH:mm'));
	public endTime = ko.pureComputed(() =>
		this._endTime() ? format(this._endTime(), 'dd/MM/yyyy') : null,
	);

	public status = ko.pureComputed(() => {
		if (this._status() == MeetingStatus.InProgress)
			return R.ConversationInProgress;

		return StringUtils.format(
			R.ConversationStartedInfo,
			formatDistanceToNow(this.firstCurrentUserJoinedOn(), new Date(), {
				locale: LocaleUtils.getLocale(),
			}),
		);
	});

	public isWaiting = ko.pureComputed(
		() => this._status() == MeetingStatus.Waiting,
	);

	constructor(meetingDto: MeetingDto) {
		this.createdOn(new Date());
		this.fromDto(meetingDto);
	}

	public joinMeeting() {
		if (this._participants().find((p) => p.isCaregiver)) {
			this.caregiverHasJoined(true);
		} else {
			this.caregiverHasJoined(false);
		}
		MicroModal.show('confirm-join');
	}

	public confirmJoinMeeting() {
		const caregiverFirstNames = this._participants()
			.filter((p) => p.isCaregiver)
			.map((p) => p.displayName.split(' ')[0].toLowerCase());

		if (
			this.caregiverHasJoined() &&
			!caregiverFirstNames.includes(this.confirmJoinInput().toLowerCase())
		) {
			this.showConfirmJoinError(true);
			return;
		}
		window.location.href = this.url();
	}

	public fromDto(meetingDto: MeetingDto): void {
		this.videoRoomKey(meetingDto.videoRoomKey);
		this.url(meetingDto.videoRoomLink);
		this.cprFormatted(meetingDto.user?.cprFormatted);
		this._startTime(new Date(meetingDto.startTime));
		this._endTime(meetingDto.endTime ? new Date(meetingDto.endTime) : null);
		this._status(meetingDto.status);
		this._participants(
			meetingDto.videoSessions.map((vs) => {
				return {
					displayName: vs.displayName || 'Anonym',
					isCaregiver: vs.isCaregiver,
				};
			}),
		);
		this.lastActivity(
			meetingDto.lastActivity ? new Date(meetingDto.lastActivity) : null,
		);
		this.firstCurrentUserJoinedOn(
			meetingDto.firstCurrentUserJoinedOn
				? new Date(meetingDto.firstCurrentUserJoinedOn)
				: null,
		);
	}
}

class WaitingListViewModel {
	public calls = komapping.fromJS<MeetingViewModel>([]);

	public numberOfActiveMeetings = ko.pureComputed(
		() => this.calls().filter((c: MeetingViewModel) => !c.isWaiting()).length,
	);

	public selectedMeeting = ko.observable<MeetingViewModel>();

	public isEmpty = ko.pureComputed(() => this.calls().length == 0);

	public hasAnyEndTime = ko.pureComputed(() =>
		this.calls().some((c: MeetingViewModel) => !!c.endTime()),
	);

	public isReady = ko.observable(false);

	public setMessageVm = new SetMeetingRoomMessageModalViewModel();

	public joinMeeting = (meeting: MeetingViewModel): void => {
		this.selectedMeeting(meeting);
		meeting.joinMeeting();
	};

	public showSetMessageModal = (meeting: MeetingViewModel): void => {
		this.setMessageVm.showModal(meeting.videoRoomKey());
	};

	private readonly sound = document.getElementById('ding') as HTMLAudioElement;

	constructor() {
		this.getMeetings();

		setInterval(() => {
			this.getMeetings();
		}, 4000);

		this.calls.subscribe(
			(changes: Array<{ status: string }>) => {
				let hasNewItems = false;
				changes.forEach((change: { status: string }) => {
					if (change.status === 'added') {
						hasNewItems = true;
					}
				});
				if (hasNewItems) {
					this.playSound();
				}
			},
			null,
			'arrayChange',
		);
	}

	private playSound(): void {
		const promise = this.sound.play();
		if (promise) {
			promise.catch((error) => {
				Log.error('Could not play sound', error);
			});
		}
	}

	private getMeetings(): void {
		fetch('/api/meetings/active')
			.then(async (response) => {
				if (response.ok) {
					return response.json();
				}
				if (response.status === 401) {
					document.location.reload();
					return;
				}
				const body = await response.text();
				throw new Error(`status=${response.status} body=${body}`);
			})
			.then((json) => {
				if (!json) return;
				const o = { calls: json };
				komapping.fromJS(
					o,
					{
						calls: {
							key: (data: {
								videoRoomKey: KnockoutObservable<string>;
							}): string => {
								return ko.utils.unwrapObservable(data.videoRoomKey);
							},
							create: (options: { data: MeetingDto }): MeetingViewModel => {
								return new MeetingViewModel(options.data);
							},
							update: (options: {
								data: MeetingDto;
								target: MeetingViewModel;
							}): MeetingViewModel => {
								options.target.fromDto(options.data);
								return options.target;
							},
						},
					},
					this,
				);
				this.isReady(true);
			})
			.catch((ex) => {
				Log.error('Could not load meetings', ex);
			});
	}
}

export function WaitingList(): void {
	ko.applyBindings(
		new WaitingListViewModel(),
		document.getElementById('waiting-list'),
	);
}
